import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Divider,
  Chip,
  Collapse,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Checkbox,
  Select,
  Link,
  MenuItem,
  FormControlLabel
} from '@mui/material';
import {
  Link as RouterLink,
  useLocation,
} from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Search, FilterList } from '@mui/icons-material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
  Person,
  ArrowBack,
  ExpandMore,
  Email,
  Description
} from '@mui/icons-material';
import { cyan } from '@mui/material/colors';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { People, SelectAll } from '@mui/icons-material';

const GroupDetailsPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [tempGroupData, setTempGroupData] = useState({});
const [hasMoreUsers, setHasMoreUsers] = useState(true);
const [usersPage, setUsersPage] = useState(1);
const pageSize = 20;

  const [expanded, setExpanded] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfession, setFilterProfession] = useState('');
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [selectedGuests, setSelectedGuests] = useState([]);

  // Fonction utilitaire pour les appels API
  const apiRequest = async (endpoint, method = 'GET', body = null, headers = {}) => {
    const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}${endpoint}`;
    const accessToken = localStorage.getItem("accessToken");
    
    const options = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        ...headers
      },
    };
    
    if (body) {
      options.body = JSON.stringify(body);
    }
    
    try {
      const resp = await fetch(url, options);
      if (!resp.ok) {
        throw new Error(`HTTP error! Status: ${resp.status}`);
      }
      return await resp.json();
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  };

  // Fonction pour charger tous les utilisateurs
  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await apiRequest('/api/users/getAllUsers/');
      setAllUsers(response || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des utilisateurs',
        severity: 'error'
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Chargement des utilisateurs lors de l'ouverture de la boîte de dialogue
  useEffect(() => {
    if (openAddUserDialog) {
      fetchAllUsers();
    }
  }, [openAddUserDialog]);

  // Filtrage des utilisateurs
  useEffect(() => {
    if (!openAddUserDialog) return;

    const filtered = allUsers.filter(user => {
      // Exclure les utilisateurs déjà dans le groupe
      if (group?.users?.some(groupUser => groupUser.id === user.id)) {
        return false;
      }

      // Filtre par recherche texte (nom, email ou document)
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' || 
        (user.full_name?.toLowerCase().includes(searchLower) || 
         user.email?.toLowerCase().includes(searchLower) ||
         (user.firstname + ' ' + user.lastname).toLowerCase().includes(searchLower) ||
         (user.document_name && user.document_name.toLowerCase().includes(searchLower)));

      // Filtre par profession
      const matchesProfession = filterProfession === '' || 
        (user.profession && user.profession.toLowerCase() === filterProfession.toLowerCase());

      return matchesSearch && matchesProfession;
    });

    setFilteredUsers(filtered);
    setSelectAll(false);
  }, [allUsers, searchTerm, filterProfession, openAddUserDialog, group]);

  // Chargement des données du groupe et des utilisateurs
  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        const [groupData, groupUsers] = await Promise.all([
          apiRequest(`/api/groupes/${groupId}/`),
          apiRequest(`/api/groupes/${groupId}/users/`)
        ]);
        
        setGroup({
          ...groupData,
          users: groupUsers.users || []
        });
        setTempGroupData({
          ...groupData,
          users: groupUsers.users || []
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Erreur lors du chargement des données du groupe',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchGroupData();
  }, [groupId]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMoreUsers && !loadingUsers) {
      const nextPage = usersPage + 1;
      setUsersPage(nextPage);
      fetchAllUsers(nextPage);
    }
  };
  

  // Filtrer les membres du groupe
  const filteredMembers = group?.users?.filter(user => {
    if (!memberSearchTerm) return true;
    const searchLower = memberSearchTerm.toLowerCase();
    return (
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.firstname && user.firstname.toLowerCase().includes(searchLower)) ||
      (user.lastname && user.lastname.toLowerCase().includes(searchLower)) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchLower))
    );
  });

  // Gestion des modifications du groupe
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setTempGroupData({ ...tempGroupData, [name]: value });
  };

  const saveChanges = async () => {
    setIsSaving(true);
    try {
      const updatedGroup = await apiRequest(`/api/groupes/update/${groupId}/`, 'PUT', {
        nom: tempGroupData.nom,
        description: tempGroupData.description,
        color: tempGroupData.color
      });

      setGroup(updatedGroup);
      setIsEditing(false);
      
      setSnackbar({
        open: true,
        message: 'Modifications enregistrées avec succès',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour du groupe',
        severity: 'error'
      });
      setTempGroupData(group);
    } finally {
      setIsSaving(false);
    }
  };

  // Gestion des utilisateurs
  const removeUserFromGroup = async (userId) => {
    try {
      await apiRequest(`/api/users/removeUserFromGroup/${userId}/`, 'POST', {
        groupe_id: groupId
      });

      setGroup(prev => ({
        ...prev,
        users: prev.users.filter(user => user.id !== userId)
      }));
      
      setSnackbar({
        open: true,
        message: 'Utilisateur retiré du groupe avec succès',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression de l\'utilisateur du groupe',
        severity: 'error'
      });
    }
  };

  const toggleGuestSelection = (userId) => {
    setSelectedGuests(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedGuests([]);
    } else {
      const allUserIds = filteredUsers.map(user => user.id);
      setSelectedGuests(allUserIds);
    }
    setSelectAll(!selectAll);
  };

  const addSelectedGuestsToGroup = async () => {
    try {
      for (const userId of selectedGuests) {
        await apiRequest(`/api/users/assignUserToGroup/${userId}/`, 'POST', {
          groupe_id: groupId
        });
      }
      
      // Recharger les utilisateurs du groupe
      const groupUsers = await apiRequest(`/api/groupes/${groupId}/users/`);
      setGroup(prev => ({
        ...prev,
        users: groupUsers.users || []
      }));
      
      setSnackbar({
        open: true,
        message: `${selectedGuests.length} utilisateur(s) ajouté(s) au groupe avec succès`,
        severity: 'success'
      });
      setSelectedGuests([]);
      setOpenAddUserDialog(false);
      setSearchTerm('');
      setFilterProfession('');
      setSelectAll(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'ajout des utilisateurs au groupe',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!group) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        p: 3,
        mt:4,
        textAlign: 'center'
      }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Groupe non trouvé
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/groupes')}
          startIcon={<ArrowBack />}
        >
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3, mt: 2 }}>
      <RouterLink
        to={`/${globalThis.localStorage.getItem('tenant')}/dashboard/group`}
        style={{ textDecoration: "none" }}
      >
        <Link
          color="textPrimary"
          component="a"
          sx={{
            alignItems: "center",
            display: "flex",
            marginTop: "30px",
            mt: 4
          }}
        >
          <ArrowBackIcon fontSize="small" sx={{ mr: 1, ml: 4 }} />
          <Typography variant="subtitle2" component="span">
            Liste des groupes
          </Typography>
        </Link>
      </RouterLink>
      
      <Box sx={{ mt: 4, mb: 3, ml: 5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Détails du groupe
        </Typography>
        <Box sx={{ width: 60, height: 4, bgcolor: 'primary.main', borderRadius: 2 }} />
      </Box>

      {/* Carte d'informations du groupe */}
      <Card sx={{ mb: 3, mt: 2, mx: 2, borderRadius: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isEditing ? (
                <TextField
                  fullWidth
                  variant="standard"
                  name="nom"
                  value={tempGroupData.nom || ''}
                  onChange={handleEditChange}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '1.25rem',
                      fontWeight: 600,
                    }
                  }}
                />
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {group.nom}
                </Typography>
              )}
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 2 }}>
              {isEditing ? (
                <>
                  <Button
                    startIcon={<Cancel />}
                    onClick={() => setIsEditing(false)}
                    size="small"
                    variant="outlined"
                    color="error"
                  >
                    Annuler
                  </Button>
                  <Button
                    startIcon={<Save />}
                    onClick={saveChanges}
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                  >
                    {isSaving ? <CircularProgress size={24} /> : 'Enregistrer'}
                  </Button>
                </>
              ) : (
                <Button
                  startIcon={<Edit />}
                  onClick={() => setIsEditing(true)}
                  size="small"
                  variant="outlined"
                  color="primary"
                >
                  Modifier
                </Button>
              )}
            </Box>
          }
        />
        <CardContent>
          {isEditing ? (
            <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
              <TextField
                fullWidth
                multiline
                label="Description"
                name="description"
                value={tempGroupData.description || ''}
                onChange={handleEditChange}
                rows={3}
                InputProps={{
                  sx: { fontWeight: 200 }
                }}
                InputLabelProps={{
                  sx: { fontWeight: 200 }
                }}
              />
            </Box>
          ) : (
            <Typography variant="body1" sx={{ 
              lineHeight: 1.6,
              color: 'text.secondary',
              fontWeight: 400
            }}>
              {group.description || 'Aucune description'}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Section Utilisateurs */}
      <Card sx={{ mb: 3, mt: 2, mx: 2, borderRadius: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                onClick={() => setExpanded(!expanded)}
                sx={{
                  transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.3s ease',
                }}
              >
                <ExpandMore />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Membres
              </Typography>
              <Chip 
                sx={{ ml: 2, mt: 0.8 }}
                label={`${group.users?.length || 0}`}
                size="small"
                icon={<People sx={{ fontSize: 16 }} />}
              />
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="Rechercher membres..."
                variant="outlined"
                size="small"
                value={memberSearchTerm}
                onChange={(e) => setMemberSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Button
                startIcon={<Add />}
                onClick={() => {
                  setOpenAddUserDialog(true);
                  setSearchTerm('');
                  setFilterProfession('');
                }}
                size="small"
                variant="contained"
                color="primary"
              >
                Ajouter des membres
              </Button>
            </Box>
          }
        />
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            {filteredMembers?.length > 0 ? (
              <List sx={{ 
                bgcolor: 'background.paper',
                borderRadius: '8px',
                overflow: 'hidden',
              }}>
                {filteredMembers.map((user, index) => (
                  <React.Fragment key={user.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => removeUserFromGroup(user.id)}
                          color="default"
                        >
                          <Delete />
                        </IconButton>
                      }
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        const tenant = localStorage.getItem('tenant');
                        navigate(`/${tenant}/dashboard/customers/${user.id}`);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: cyan[500] }}>
                          {user.full_name?.charAt(0) || <Person />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography fontWeight="500">
                            {user.full_name || `${user.firstname} ${user.lastname}` || user.email}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                            {user.profession && (
                              <Chip 
                                label={user.profession} 
                                size="small" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredMembers.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 4,
                textAlign: 'center'
              }}>
                <Person sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {memberSearchTerm ? 'Aucun membre ne correspond à votre recherche' : 'Aucun membre dans ce groupe'}
                </Typography>
                {!memberSearchTerm && (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Ajoutez des membres en cliquant sur le bouton "Ajouter des membres"
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* Dialog pour ajouter des utilisateurs */}
      <Dialog
        open={openAddUserDialog}
        onClose={() => {
          setOpenAddUserDialog(false);
          setSelectedGuests([]);
          setSelectAll(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Ajouter des membres au groupe
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              placeholder="Rechercher par nom, email ou document"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <Select
              value={filterProfession}
              onChange={(e) => setFilterProfession(e.target.value)}
              displayEmpty
              sx={{ minWidth: 180 }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterList />
                </InputAdornment>
              }
            >
              <MenuItem value="">Toutes les professions</MenuItem>
              {[...new Set(allUsers.map(user => user.profession))].filter(Boolean).map((profession) => (
                <MenuItem key={profession} value={profession}>
                  {profession}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ mb: 1, ml: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  indeterminate={
                    selectedGuests.length > 0 && 
                    selectedGuests.length < filteredUsers.length
                  }
                  icon={<SelectAll />}
                />
              }
              label={`Sélectionner tout (${selectedGuests.length}/${filteredUsers.length})`}
            />
          </Box>
          
          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{
              bgcolor: 'background.paper',
              borderRadius: '8px',
              overflowY: 'auto',
              maxHeight: 'calc(70vh - 150px)',
            }}>
              {filteredUsers.map((user) => (
                <ListItem key={user.id}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: cyan[500] }}>
                      {user.firstname?.charAt(0)}{user.lastname?.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${user.firstname} ${user.lastname}`}
                    secondary={
                      <>
                        {user.email}
                        {user.profession && ` • ${user.profession}`}
                        {user.document_name && ` • Document: ${user.document_name}`}
                      </>
                    }
                  />
                  <Checkbox
                    checked={selectedGuests.includes(user.id)}
                    onChange={() => toggleGuestSelection(user.id)}
                  />
                </ListItem>
              ))}
              {!loadingUsers && filteredUsers.length === 0 && (
                <Typography variant="body1" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  Aucun utilisateur trouvé
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenAddUserDialog(false);
              setSelectedGuests([]);
              setSelectAll(false);
            }} 
            color="error"
            variant="outlined"
          >
            Fermer
          </Button>
          {selectedGuests.length > 0 && (
            <Button
              onClick={addSelectedGuestsToGroup}
              startIcon={<Add />}
              variant="contained"
              color="primary"
              sx={{ ml: 2 }}
            >
              Ajouter {selectedGuests.length} membre(s)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupDetailsPage;